/**
 * Pixel Tracking Service
 * Facebook, Google Analytics, TikTok pixel management
 * Only fires in Production mode
 */

import { createLogger } from '../core/logger';
import { isTrackingEnabled, isProductionMode } from '../core/config';
import { TrackingConfig } from '../types';

const logger = createLogger('Pixels');

let initialized = false;
let config: TrackingConfig | null = null;

// Declare global window properties for pixels
declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    ttq?: {
      load: (id: string) => void;
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

/**
 * Initialize pixel tracking
 * Only loads pixels in Production mode
 */
export function initializePixels(trackingConfig: TrackingConfig): void {
  if (initialized) return;

  config = trackingConfig;

  if (!isTrackingEnabled()) {
    logger.info('Tracking disabled - pixels will not be loaded');
    initialized = true;
    return;
  }

  logger.info('Initializing pixels', {
    hasFacebook: !!config.facebookPixelID,
    hasGoogle: !!config.googleAnalyticsID || !!config.googleTagManagerID,
    hasTikTok: !!config.tiktokPixelID
  });

  // Initialize Facebook Pixel
  if (config.facebookPixelID) {
    initFacebookPixel(config.facebookPixelID);
  }

  // Initialize Google Tag Manager
  if (config.googleTagManagerID) {
    initGoogleTagManager(config.googleTagManagerID);
  }

  // Initialize Google Analytics (if no GTM)
  if (config.googleAnalyticsID && !config.googleTagManagerID) {
    initGoogleAnalytics(config.googleAnalyticsID);
  }

  // Initialize TikTok Pixel
  if (config.tiktokPixelID) {
    initTikTokPixel(config.tiktokPixelID);
  }

  initialized = true;
}

/**
 * Initialize Facebook Pixel
 */
function initFacebookPixel(pixelId: string): void {
  logger.debug('Initializing Facebook Pixel', { pixelId });

  // Check if already initialized by server-side injection
  if (window.fbq) {
    logger.info('Facebook Pixel already loaded (server-side injection)');
    return;
  }

  // Facebook Pixel base code
  /* eslint-disable */
  (function(f: any, b: Document, e: string, v: string, n?: any, t?: HTMLScriptElement, s?: Element) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s?.parentNode?.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fbq = window.fbq as any;
  fbq?.('init', pixelId);
  fbq?.('track', 'PageView');

  logger.info('Facebook Pixel initialized');
}

/**
 * Initialize Google Tag Manager
 */
function initGoogleTagManager(containerId: string): void {
  logger.debug('Initializing Google Tag Manager', { containerId });

  window.dataLayer = window.dataLayer || [];

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);

  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });

  logger.info('Google Tag Manager initialized');
}

/**
 * Initialize Google Analytics 4
 */
function initGoogleAnalytics(measurementId: string): void {
  logger.debug('Initializing Google Analytics', { measurementId });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);

  logger.info('Google Analytics initialized');
}

/**
 * Initialize TikTok Pixel
 */
function initTikTokPixel(pixelId: string): void {
  logger.debug('Initializing TikTok Pixel', { pixelId });

  /* eslint-disable */
  (function(w: any, _d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = w[t] = w[t] || [];
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function(t: any, e: string) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function(t: string) {
      const e = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
      return e;
    };
    ttq.load = function(e: string, n?: any) {
      const i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      ttq._i[e]._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      const o = document.createElement('script');
      o.type = 'text/javascript';
      o.async = true;
      o.src = i + '?sdkid=' + e + '&lib=' + t;
      const a = document.getElementsByTagName('script')[0];
      a?.parentNode?.insertBefore(o, a);
    };
    ttq.load(pixelId);
    ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable */

  logger.info('TikTok Pixel initialized');
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, data?: Record<string, unknown>): void {
  if (!isTrackingEnabled()) {
    logger.debug('Event logged (tracking disabled)', { eventName, data });
    return;
  }

  logger.debug('Tracking event', { eventName, data });

  // Facebook
  if (window.fbq) {
    window.fbq('trackCustom', eventName, data);
  }

  // Google Analytics
  if (window.gtag) {
    window.gtag('event', eventName, data);
  }

  // TikTok
  if (window.ttq) {
    window.ttq.track(eventName, data);
  }
}

/**
 * Track quiz start
 */
export function trackQuizStart(): void {
  trackEvent('QuizStart');
}

/**
 * Track quiz progress
 */
export function trackQuizProgress(questionIndex: number, totalQuestions: number): void {
  const percentage = Math.round(((questionIndex + 1) / totalQuestions) * 100);
  trackEvent('QuizProgress', {
    question_index: questionIndex,
    total_questions: totalQuestions,
    completion_percentage: percentage
  });
}

/**
 * Track quiz completion
 */
export function trackQuizComplete(): void {
  trackEvent('QuizComplete');

  // Also fire standard CompleteRegistration for Facebook
  if (isProductionMode() && window.fbq) {
    window.fbq('track', 'CompleteRegistration');
  }
}

/**
 * Track lead submission
 */
export function trackLead(data: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  leadID?: string;
}): void {
  if (!isTrackingEnabled()) {
    logger.debug('Lead event logged (tracking disabled)', data);
    return;
  }

  logger.info('Tracking Lead event');

  // Facebook Lead event
  if (window.fbq) {
    window.fbq('track', 'Lead', {
      content_name: 'Quiz Lead',
      content_category: 'Lead',
      lead_event_source: 'quiz_funnel'
    });
  }

  // Google Analytics
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      currency: 'USD',
      value: 0
    });
  }

  // TikTok
  if (window.ttq) {
    window.ttq.track('SubmitForm');
  }
}

/**
 * Track Smart Path shown
 */
export function trackSmartPathShown(pathId: string, pathType: 'soft' | 'hard'): void {
  trackEvent('SmartPathShown', { path_id: pathId, path_type: pathType });
}
